import { getStatusDisplayText } from './formManagementStatusBadge';
import { FormData } from './types';

type SortableFormKey = keyof Pick<
  FormData,
  | 'title'
  | 'category'
  | 'country'
  | 'languages'
  | 'questionsCount'
  | 'createdBy'
  | 'createdAt'
  | 'lastUpdated'
  | 'status'
>;

export const FORM_MANAGEMENT_SORT_LABEL_TO_KEY: Record<
  string,
  SortableFormKey
> = {
  'Form title': 'title',
  Category: 'category',
  Countries: 'country',
  Languages: 'languages',
  Questions: 'questionsCount',
  Status: 'status',
  'Created by': 'createdBy',
  'Created at': 'createdAt',
  'Last updated': 'lastUpdated',
};

export const SORTABLE_COLUMNS: string[] = [
  'Form title',
  'Category',
  'Countries',
  'Languages',
  'Questions',
  'Status',
  'Created by',
  'Created at',
  'Last updated',
];

function compareStrings(
  a: string,
  b: string,
  direction: 'asc' | 'desc',
): number {
  const normalizedA = a.trim().toLowerCase();
  const normalizedB = b.trim().toLowerCase();
  const cmp = normalizedA.localeCompare(normalizedB, undefined, {
    sensitivity: 'base',
  });
  return direction === 'asc' ? cmp : -cmp;
}

function compareNumbers(
  a: number,
  b: number,
  direction: 'asc' | 'desc',
): number {
  const cmp = a - b;
  return direction === 'asc' ? cmp : -cmp;
}

export function sortForms(
  forms: FormData[],
  sortColumnLabel: string | null,
  sortDirection: 'asc' | 'desc',
): FormData[] {
  if (!sortColumnLabel || forms.length === 0) return forms;
  const sortKey = FORM_MANAGEMENT_SORT_LABEL_TO_KEY[sortColumnLabel];
  if (!sortKey) return forms;

  if (sortKey === 'questionsCount') {
    return [...forms].sort((a, b) =>
      compareNumbers(a.questionsCount, b.questionsCount, sortDirection),
    );
  }

  if (sortKey === 'country') {
    return [...forms].sort((a, b) =>
      compareNumbers(a.countryCount, b.countryCount, sortDirection),
    );
  }

  if (sortKey === 'status') {
    return [...forms].sort((a, b) =>
      compareStrings(
        getStatusDisplayText(a.status),
        getStatusDisplayText(b.status),
        sortDirection,
      ),
    );
  }

  return [...forms].sort((a, b) => {
    const valA = a[sortKey];
    const valB = b[sortKey];
    const strA = valA == null ? '' : String(valA);
    const strB = valB == null ? '' : String(valB);
    return compareStrings(strA, strB, sortDirection);
  });
}
